import type { ProposalApprovalSuccessPayload } from "@shared/proposalApprovalSuccess";
import { PROPOSAL_APPROVAL_SUCCESS_STORAGE_KEY } from "@shared/proposalApprovalSuccess";
import { secureProposalLinkBApprovedPath } from "@shared/proposalLinkBUrl";

export function saveProposalApprovalSuccess(payload: ProposalApprovalSuccessPayload): void {
  try {
    sessionStorage.setItem(PROPOSAL_APPROVAL_SUCCESS_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Reads the single-slot session cache written immediately after approval (same tab, same
 * session). When `proposalId` is provided, the cached payload must match it — used by the
 * legacy numeric success route. The secure Link B success route has no numeric id to match
 * against up front, so it reads whatever is cached (already scoped to this session/tab).
 */
export function readProposalApprovalSuccess(proposalId?: number): ProposalApprovalSuccessPayload | null {
  try {
    const raw = sessionStorage.getItem(PROPOSAL_APPROVAL_SUCCESS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ProposalApprovalSuccessPayload;
    if (proposalId != null && parsed?.proposalId !== proposalId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearProposalApprovalSuccess(): void {
  try {
    sessionStorage.removeItem(PROPOSAL_APPROVAL_SUCCESS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** @deprecated Legacy numeric-id success path — never emit for public/customer links. */
export function proposalApprovalSuccessPath(proposalId: number): string {
  return `/proposal/${proposalId}/approved`;
}

/** Secure Link B success path — the only customer-facing approval success URL. */
export function proposalApprovalSuccessPathForCredential(credential: {
  readableRef: string;
  sequence: number;
  token: string;
}): string {
  return secureProposalLinkBApprovedPath({
    proposalRef: credential.readableRef,
    issuanceSequence: credential.sequence,
    token: credential.token,
  });
}
